import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Anchor, Activity, Package, ShoppingCart, FileText, Shield,
  Globe, Clock, CheckCircle, Zap, Users, ArrowRight,
  ChevronRight, Star
} from 'lucide-react';

// SVG icon components (inline to avoid lucide import conflicts)
const WOIcon = (props) => <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/><line x1="9" y1="12" x2="15" y2="12"/><line x1="9" y1="16" x2="11" y2="16"/></svg>;
const AIMaintenance = (props) => <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>;
const InvIcon = (props) => <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>;
const PurIcon = (props) => <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>;
const CertIcon = (props) => <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></svg>;
const RepIcon = (props) => <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/></svg>;
const StarFill = (props) => <svg {...props} viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>;

const FEATURES = [
  {
    icon: WOIcon,
    title: 'Work Order Management',
    desc: 'Full lifecycle tracking from Open to Closed. Assign, prioritize, and sign-off with complete audit trail.',
    color: '#3B6082',
    colorBg: '#EBF3F9',
  },
  {
    icon: AIMaintenance,
    title: 'AI Predictive Maintenance',
    desc: 'Health score monitoring and AI-generated job predictions catch failures before they happen.',
    color: '#7c3aed',
    colorBg: '#ede9fe',
  },
  {
    icon: InvIcon,
    title: 'Inventory & Stock Control',
    desc: 'Real-time stock levels, critical spares tracking, reorder alerts, and location management.',
    color: '#059669',
    colorBg: '#d1fae5',
  },
  {
    icon: PurIcon,
    title: 'Procurement Workflow',
    desc: 'Requisitions → Purchase Orders → Goods Receipt. Full traceability from request to delivery.',
    color: '#d97706',
    colorBg: '#fef3c7',
  },
  {
    icon: CertIcon,
    title: 'Certificate Management',
    desc: 'Track statutory certificates, renewal reminders, and expiry alerts across your entire fleet.',
    color: '#0284c7',
    colorBg: '#e0f2fe',
  },
  {
    icon: RepIcon,
    title: 'Reports & Compliance',
    desc: '18+ report types covering maintenance, stock, procurement, and regulatory compliance.',
    color: '#e74c3c',
    colorBg: '#fdecea',
  },
];

const STATS = [
  { value: '18+', label: 'Report Types' },
  { value: '100%', label: 'Ship-Shore Sync' },
  { value: 'AI+', label: 'Predictive Engine' },
  { value: 'Zero', label: 'Downtime' },
];

const TESTIMONIALS = [
  {
    name: 'Capt. James Hartley',
    role: 'Chief Engineer, MV Pacific Voyager',
    text: 'MARISYNC transformed how we manage maintenance at sea. Everything from work orders to stock is right at our fingertips.',
    avatar: 'JH',
    color: '#3B6082',
  },
  {
    name: 'Sarah Chen',
    role: 'Fleet Superintendent, iShip Solutions',
    text: 'The shore dashboard gives me real-time visibility across all vessels. AI predictions have caught three major failures before they happened.',
    avatar: 'SC',
    color: '#059669',
  },
  {
    name: 'Ahmed Al-Rashid',
    role: 'Technical Manager, Gulf Fleet',
    text: 'Certificate tracking alone has saved us from two Port State Control detentions. Highly recommended for any maritime operation.',
    avatar: 'AR',
    color: '#7c3aed',
  },
];

const ROLES = [
  { name: 'Chief Engineer', desc: 'Full operational control' },
  { name: '2nd / 3rd Engineer', desc: 'Mobile-friendly task view' },
  { name: 'Electrical Officer', desc: 'Equipment-specific dashboards' },
  { name: 'Fleet Superintendent', desc: 'Multi-vessel shore overview' },
  { name: 'Procurement Officer', desc: 'Requisition & PO management' },
  { name: 'Shore Admin', desc: 'System & user administration' },
];

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div style={{ background: '#f8fafc', minHeight: '100vh', fontFamily: "'Inter', -apple-system, sans-serif" }}>

      {/* ── NAVBAR ──────────────────────────────────────────────── */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0,
        background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(12px)',
        borderBottom: '1px solid #e5e7eb',
        zIndex: 500, padding: '0 48px', height: 64,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src={process.env.PUBLIC_URL + '/logo.png'} alt="iSHIP+" style={{ height: 30, objectFit: 'contain' }} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#3B6082', letterSpacing: 0.5 }}>iSHIP+ MARISYNC</div>
            <div style={{ fontSize: 9, color: '#9ca3af', letterSpacing: 1.5, textTransform: 'uppercase' }}>Planned Maintenance System</div>
          </div>
        </div>

        {/* Nav links */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {['Features', 'Reports', 'Security', 'Contact'].map(link => (
            <a key={link} href={`#${link.toLowerCase()}`} style={{
              padding: '8px 14px', fontSize: 13, color: '#6b7280', textDecoration: 'none',
              borderRadius: 7, transition: 'all 0.15s', fontWeight: 500,
            }}
              onMouseEnter={e => { e.currentTarget.style.background = '#f4f6f9'; e.currentTarget.style.color = '#1a1a2e'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#6b7280'; }}
            >
              {link}
            </a>
          ))}
        </div>

        {/* CTA */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            onClick={() => navigate('/login')}
            style={{
              padding: '8px 20px', fontSize: 13, fontWeight: 600, color: '#3B6082',
              background: '#EBF3F9', border: 'none', borderRadius: 8, cursor: 'pointer',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#dbeafe'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#EBF3F9'; }}
          >
            Sign In
          </button>
          <button
            onClick={() => navigate('/login')}
            style={{
              padding: '8px 20px', fontSize: 13, fontWeight: 600, color: 'white',
              background: '#3B6082', border: 'none', borderRadius: 8, cursor: 'pointer',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#527A9E'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#3B6082'; }}
          >
            Get Started
          </button>
        </div>
      </nav>

      {/* ── HERO ───────────────────────────────────────────────── */}
      <section style={{
        paddingTop: 160, paddingBottom: 100,
        background: 'linear-gradient(180deg, #f8fafc 0%, #EBF3F9 40%, #f8fafc 100%)',
        textAlign: 'center', position: 'relative', overflow: 'hidden',
      }}>
        {/* Background decoration */}
        <div style={{ position: 'absolute', top: -60, left: '50%', transform: 'translateX(-50%)', width: 800, height: 800, borderRadius: '50%', background: 'radial-gradient(circle, rgba(59,96,130,0.06) 0%, transparent 70%)', pointerEvents: 'none' }} />

        {/* Badge */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'white', border: '1px solid #e5e7eb',
          borderRadius: 100, padding: '6px 16px', fontSize: 12, fontWeight: 600,
          color: '#3B6082', marginBottom: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
        }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#27ae60', display: 'inline-block' }} />
          Maritime PMS · Trusted by 50+ vessels globally
        </div>

        {/* Headline */}
        <h1 style={{
          fontSize: 'clamp(36px, 5vw, 60px)', fontWeight: 800, lineHeight: 1.1,
          color: '#1a1a2e', margin: '0 auto 20px', maxWidth: 760, letterSpacing: '-0.5px',
        }}>
          The Maritime PMS Built for{' '}
          <span style={{ color: '#3B6082' }}>Real Operations</span>
        </h1>

        <p style={{
          fontSize: 18, color: '#6b7280', maxWidth: 580,
          margin: '0 auto 40px', lineHeight: 1.6,
        }}>
          Manage work orders, maintenance plans, inventory, and procurement —
          all from ship to shore. Powered by AI-driven predictive maintenance.
        </p>

        {/* CTAs */}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={() => navigate('/login')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '14px 28px', fontSize: 15, fontWeight: 700, color: 'white',
              background: '#3B6082', border: 'none', borderRadius: 10, cursor: 'pointer',
              transition: 'all 0.15s', boxShadow: '0 4px 14px rgba(59,96,130,0.3)',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#527A9E'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#3B6082'; e.currentTarget.style.transform = 'none'; }}
          >
            Access Demo <ArrowRight size={16} />
          </button>
          <button
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '14px 28px', fontSize: 15, fontWeight: 600, color: '#3B6082',
              background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, cursor: 'pointer',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#85A3C1'; e.currentTarget.style.background = '#EBF3F9'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = 'white'; }}
          >
            Watch Overview
          </button>
        </div>

        {/* Social proof */}
        <div style={{ marginTop: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 0 }}>
            {['#JH', '#SC', '#AR', '#TL', '#MK'].map((a, i) => (
              <div key={a} style={{
                width: 32, height: 32, borderRadius: '50%', background: `hsl(${i * 72 + 200}, 40%, 50%)`,
                border: '2px solid white', marginLeft: i > 0 ? -8 : 0, display: 'flex',
                alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 9, fontWeight: 700,
              }}>{a}</div>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {[1,2,3,4,5].map(n => <StarFill key={n} size={12} style={{ color: '#f59e0b', fill: '#f59e0b' }} />)}
          </div>
          <span style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>
            <strong style={{ color: '#1a1a2e' }}>4.9/5</strong> from 200+ maritime professionals
          </span>
        </div>

        {/* Hero screenshot mockup */}
        <div style={{
          margin: '60px auto 0', maxWidth: 1000,
          background: 'white', borderRadius: 14,
          border: '1px solid #e5e7eb',
          boxShadow: '0 20px 60px rgba(0,0,0,0.1), 0 4px 16px rgba(0,0,0,0.06)',
          overflow: 'hidden',
        }}>
          <div style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
            {['#e74c3c','#f39c12','#27ae60'].map(c => (
              <div key={c} style={{ width: 10, height: 10, borderRadius: '50%', background: c }} />
            ))}
            <div style={{ flex: 1, textAlign: 'center', fontSize: 11, color: '#9ca3af', fontWeight: 500 }}>
              iSHIP+ MARISYNC — MV Ocean Pioneer Dashboard
            </div>
          </div>
          {/* Dashboard preview placeholder */}
          <div style={{ padding: 24 }}>
            {/* Mini stat cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
              {[
                { label: 'Overdue Jobs', value: '3', color: '#e74c3c', bg: '#fdecea' },
                { label: 'Due This Week', value: '7', color: '#f39c12', bg: '#fef9e7' },
                { label: 'AI Alerts', value: '2', color: '#7c3aed', bg: '#ede9fe' },
                { label: 'Open Defects', value: '5', color: '#3498db', bg: '#ebf5fb' },
              ].map(s => (
                <div key={s.label} style={{ background: s.bg, borderRadius: 10, padding: '16px', textAlign: 'center' }}>
                  <div style={{ fontSize: 28, fontWeight: 800, color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: s.color, fontWeight: 600, marginTop: 4 }}>{s.label}</div>
                </div>
              ))}
            </div>
            {/* Mini table */}
            <div style={{ background: '#f8fafc', borderRadius: 10, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: 0, padding: '10px 14px', background: '#f1f5f9', fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                <span>Job Title</span><span>Priority</span><span>Due</span><span>Status</span><span>Assignee</span>
              </div>
              {[
                { title: 'Lube Oil Change — Main Engine', prio: 'High', prioColor: '#e74c3c', prioBg: '#fdecea', due: 'Today', status: 'Open', statusBg: '#ebf5fb', statusColor: '#3498db', assign: 'C.E Wilson' },
                { title: 'Turbocharger Inspection', prio: 'Medium', prioColor: '#f39c12', prioBg: '#fef9e7', due: 'Overdue', status: 'In Progress', statusBg: '#fef9e7', statusColor: '#f39c12', assign: 'C.E Wilson' },
                { title: 'Fire Pump Monthly Test', prio: 'High', prioColor: '#e74c3c', prioBg: '#fdecea', due: '5 days', status: 'Pending Close', statusBg: '#fef3c7', statusColor: '#d97706', assign: 'A. Hassan' },
              ].map((row, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: 0, padding: '11px 14px', borderTop: '1px solid #f1f5f9', fontSize: 12, alignItems: 'center' }}>
                  <span style={{ color: '#1a1a2e', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.title}</span>
                  <span style={{ background: row.prioBg, color: row.prioColor, padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, textAlign: 'center' }}>{row.prio}</span>
                  <span style={{ color: row.due === 'Overdue' ? '#e74c3c' : row.due === 'Today' ? '#d97706' : '#6b7280', fontSize: 12, fontWeight: row.due === 'Overdue' ? 700 : 400 }}>{row.due}</span>
                  <span style={{ background: row.statusBg, color: row.statusColor, padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700 }}>{row.status}</span>
                  <span style={{ color: '#6b7280', fontSize: 12 }}>{row.assign}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS ──────────────────────────────────────────────── */}
      <section id="features" style={{ padding: '80px 48px', background: '#3B6082' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0 }}>
          {STATS.map((s, i) => (
            <div key={i} style={{
              textAlign: 'center', padding: '24px',
              borderRight: i < 3 ? '1px solid rgba(255,255,255,0.15)' : 'none',
            }}>
              <div style={{ fontSize: 42, fontWeight: 800, color: 'white', lineHeight: 1 }}>{s.value}</div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', marginTop: 8, fontWeight: 500 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── FEATURES ────────────────────────────────────────────── */}
      <section style={{ padding: '100px 48px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <div style={{ display: 'inline-block', background: '#EBF3F9', color: '#3B6082', padding: '4px 12px', borderRadius: 100, fontSize: 11, fontWeight: 700, marginBottom: 16, letterSpacing: 1, textTransform: 'uppercase' }}>
              Core Modules
            </div>
            <h2 style={{ fontSize: 36, fontWeight: 800, color: '#1a1a2e', margin: '0 0 16px', letterSpacing: '-0.5px' }}>
              Everything Your Fleet Needs
            </h2>
            <p style={{ fontSize: 16, color: '#6b7280', maxWidth: 520, margin: '0 auto' }}>
              Six integrated modules covering the entire maintenance lifecycle — from component to certificate.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} style={{
                  background: 'white', borderRadius: 14, border: '1px solid #e5e7eb',
                  padding: 28, transition: 'all 0.2s',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = f.color; e.currentTarget.style.boxShadow = `0 4px 20px rgba(0,0,0,0.08)`; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)'; e.currentTarget.style.transform = 'none'; }}
                >
                  <div style={{
                    width: 52, height: 52, borderRadius: 12,
                    background: f.colorBg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginBottom: 18,
                  }}>
                    <Icon size={26} style={{ color: f.color }} />
                  </div>
                  <h3 style={{ fontSize: 17, fontWeight: 700, color: '#1a1a2e', margin: '0 0 8px' }}>{f.title}</h3>
                  <p style={{ fontSize: 14, color: '#6b7280', margin: 0, lineHeight: 1.6 }}>{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── ROLE-BASED ACCESS ──────────────────────────────────── */}
      <section style={{ padding: '80px 48px', background: '#f8fafc', borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 64, alignItems: 'center' }}>
          <div>
            <div style={{ display: 'inline-block', background: '#ede9fe', color: '#7c3aed', padding: '4px 12px', borderRadius: 100, fontSize: 11, fontWeight: 700, marginBottom: 16, letterSpacing: 1, textTransform: 'uppercase' }}>
              Role-Based Access
            </div>
            <h2 style={{ fontSize: 32, fontWeight: 800, color: '#1a1a2e', margin: '0 0 16px', letterSpacing: '-0.5px' }}>
              Built for Every Maritime Role
            </h2>
            <p style={{ fontSize: 15, color: '#6b7280', margin: '0 0 32px', lineHeight: 1.7 }}>
              Whether you're the Chief Engineer at sea or the Fleet Superintendent on shore,
              MARISYNC adapts to your workflow with role-specific views and permissions.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {ROLES.map((r, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <CheckCircle size={16} style={{ color: '#27ae60', flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e' }}>{r.name}</div>
                    <div style={{ fontSize: 11, color: '#9ca3af' }}>{r.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div style={{
            background: 'white', borderRadius: 14, border: '1px solid #e5e7eb',
            overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          }}>
            {/* Simulated sidebar nav */}
            <div style={{ background: '#3B6082', padding: '20px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <img src={process.env.PUBLIC_URL + '/logo.png'} alt="iSHIP+" style={{ height: 24, objectFit: 'contain' }} />
            </div>
            <div style={{ padding: 8 }}>
              {[
                { label: 'Dashboard', active: true, color: '#3B6082' },
                { label: 'Work Orders', active: false },
                { label: 'Inventory', active: false },
                { label: 'Purchasing', active: false },
                { label: 'Reports', active: false },
                { label: 'Settings', active: false },
              ].map((item, i) => (
                <div key={i} style={{
                  padding: '10px 14px', borderRadius: 8, fontSize: 13, fontWeight: item.active ? 600 : 500,
                  color: item.active ? '#3B6082' : '#6b7280',
                  background: item.active ? '#EBF3F9' : 'transparent',
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 8,
                  marginBottom: 2,
                }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: item.active ? '#3B6082' : '#d1d5db', flexShrink: 0 }} />
                  {item.label}
                </div>
              ))}
            </div>
            <div style={{ padding: '12px 14px', borderTop: '1px solid #f1f5f9' }}>
              <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6, fontWeight: 600 }}>LOGGED IN AS</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg, #85A3C1, #3B6082)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 10, fontWeight: 700 }}>JW</div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#1a1a2e' }}>James Wilson</div>
                  <div style={{ fontSize: 10, color: '#3B6082', fontWeight: 700 }}>CHIEF ENGINEER</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ───────────────────────────────────────── */}
      <section style={{ padding: '100px 48px', background: '#f8fafc', borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#fff8e7', border: '1px solid #fde68a', borderRadius: 100, padding: '5px 14px', fontSize: 11, fontWeight: 700, color: '#b45309', marginBottom: 16 }}>
              <div style={{ display: 'flex', gap: 1 }}>
                {[1,2,3,4,5].map(n => <StarFill key={n} size={11} style={{ color: '#f59e0b', fill: '#f59e0b' }} />)}
              </div>
              4.9 / 5 — Rated by 200+ maritime professionals
            </div>
            <h2 style={{ fontSize: 32, fontWeight: 800, color: '#1a1a2e', margin: '0 0 12px', letterSpacing: '-0.5px' }}>
              What Our Users Say
            </h2>
            <p style={{ fontSize: 16, color: '#6b7280', maxWidth: 480, margin: '0 auto' }}>
              Real feedback from Chief Engineers, Fleet Superintendents, and Technical Managers at sea and on shore.
            </p>
          </div>

          {/* Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
            {TESTIMONIALS.map((t, i) => (
              <div key={i} style={{
                background: 'white', borderRadius: 16, border: '1px solid #e5e7eb',
                padding: '28px 28px 24px', boxShadow: '0 1px 6px rgba(0,0,0,0.04)',
                transition: 'all 0.2s', position: 'relative', overflow: 'hidden',
              }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 30px rgba(59,96,130,0.12)'; e.currentTarget.style.borderColor = '#c5d3e0'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 6px rgba(0,0,0,0.04)'; e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.transform = 'none'; }}
              >
                {/* Quote mark */}
                <div style={{ fontSize: 72, color: '#EBF3F9', fontFamily: 'Georgia, serif', lineHeight: 1, position: 'absolute', top: 12, left: 20, userSelect: 'none' }}>"</div>

                {/* Stars + Rating */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, position: 'relative' }}>
                  <div style={{ display: 'flex', gap: 2 }}>
                    {[1,2,3,4,5].map(n => <StarFill key={n} size={13} style={{ color: '#f59e0b', fill: '#f59e0b' }} />)}
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#b45309', background: '#fff8e7', padding: '2px 8px', borderRadius: 100, border: '1px solid #fde68a' }}>Verified</span>
                </div>

                {/* Quote */}
                <p style={{ fontSize: 14, color: '#374151', margin: '0 0 24px', lineHeight: 1.75, position: 'relative' }}>
                  {t.text}
                </p>

                {/* Divider */}
                <div style={{ height: 1, background: '#f1f5f9', marginBottom: 18 }} />

                {/* Author */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 12, background: `linear-gradient(135deg, ${t.color}, ${t.color}cc)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'white', fontSize: 14, fontWeight: 700, flexShrink: 0,
                    boxShadow: `0 4px 12px ${t.color}33`,
                  }}>
                    {t.avatar}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e', marginBottom: 2 }}>{t.name}</div>
                    <div style={{ fontSize: 11, color: '#9ca3af', lineHeight: 1.3 }}>{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────────── */}
      <section style={{
        padding: '80px 48px', background: 'linear-gradient(135deg, #3B6082 0%, #2D4D68 100%)',
        textAlign: 'center',
      }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 100, padding: '6px 16px', fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.8)', marginBottom: 24 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#27ae60' }} />
            Live Demo Available
          </div>
          <h2 style={{ fontSize: 38, fontWeight: 800, color: 'white', margin: '0 0 16px', letterSpacing: '-0.5px' }}>
            Ready to Digitise Your Fleet Operations?
          </h2>
          <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.7)', margin: '0 0 40px', lineHeight: 1.6 }}>
            Join 50+ vessels already using MARISYNC. Get full access to all modules
            with our demo environment — no credit card required.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => navigate('/login')}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '14px 32px', fontSize: 15, fontWeight: 700, color: '#3B6082',
                background: 'white', border: 'none', borderRadius: 10, cursor: 'pointer',
                transition: 'all 0.15s', boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'none'; }}
            >
              Access Free Demo <ArrowRight size={16} />
            </button>
            <button
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '14px 32px', fontSize: 15, fontWeight: 600, color: 'white',
                background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: 10, cursor: 'pointer', transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
            >
              Contact Sales
            </button>
          </div>
        </div>
      </section>

      {/* ── FOOTER ─────────────────────────────────────────────── */}
      <footer style={{ background: '#1a1a2e', padding: '40px 48px 32px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr', gap: 40, marginBottom: 40 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <img src={process.env.PUBLIC_URL + '/logo.png'} alt="iSHIP+" style={{ height: 28, objectFit: 'contain' }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: 'white', letterSpacing: 0.5 }}>iSHIP+</div>
                  <div style={{ fontSize: 9, color: '#6b7280', letterSpacing: 1, textTransform: 'uppercase' }}>MARISYNC</div>
                </div>
              </div>
              <p style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.7, margin: 0 }}>
                The maritime Planned Maintenance System built for modern fleet operations.
                Ship-to-shore sync, AI predictions, and full compliance tracking.
              </p>
            </div>
            {[
              { title: 'Product', links: ['Features', 'Reports', 'Integrations', 'Pricing'] },
              { title: 'Company', links: ['About', 'Contact', 'Careers', 'Blog'] },
              { title: 'Legal', links: ['Privacy', 'Terms', 'Security', 'Compliance'] },
            ].map(col => (
              <div key={col.title}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'white', marginBottom: 14, textTransform: 'uppercase', letterSpacing: 1 }}>{col.title}</div>
                {col.links.map(link => (
                  <div key={link} style={{ marginBottom: 8 }}>
                    <a href="#" style={{ fontSize: 12, color: '#6b7280', textDecoration: 'none', transition: 'color 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.color = '#e2e8f0'}
                      onMouseLeave={e => e.currentTarget.style.color = '#6b7280'}
                    >{link}</a>
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div style={{ borderTop: '1px solid #2d3748', paddingTop: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <span style={{ fontSize: 12, color: '#4b5563' }}>
              © 2026 iSHIP Solutions Pte. Ltd. All rights reserved.
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              {['LinkedIn', 'Twitter', 'YouTube'].map(s => (
                <a key={s} href="#" style={{
                  padding: '4px 12px', fontSize: 11, color: '#6b7280', textDecoration: 'none',
                  border: '1px solid #374151', borderRadius: 6,
                  transition: 'all 0.15s',
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#85A3C1'; e.currentTarget.style.color = '#85A3C1'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#374151'; e.currentTarget.style.color = '#6b7280'; }}
                >{s}</a>
              ))}
            </div>
          </div>
        </div>
      </footer>

    </div>
  );
}
